import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Empty, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import { useGapMatrix, useJobProfiles } from '@/hooks/useCompetency';
import type { GapMatrix, GapStatus } from '@/services/competency.service';
import departmentService from '@/services/department.service';
import { downloadXlsx } from '@/reports/utils/exportTable';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, GAP_COLOR } from './competencyDisplay';

const { Text } = Typography;
type Row = GapMatrix['rows'][number];
const STATUSES: GapStatus[] = ['Gap', 'Expired', 'Expiring', 'Met'];
const SHORT: Record<GapStatus, string> = { Met: 'OK', Gap: 'GAP', Expiring: 'EXP?', Expired: 'EXP' };

const GapMatrixPage: React.FC = () => {
  const navigate = useNavigate();
  const [departmentId, setDepartmentId] = useState<string>();
  const [jobProfileId, setJobProfileId] = useState<string>();
  const [status, setStatus] = useState<string>();
  const { data, isLoading } = useGapMatrix({ department_id: departmentId, job_profile_id: jobProfileId, status });
  const { data: profiles = [] } = useJobProfiles();
  const { data: departments = [] } = useQuery({ queryKey: ['departments', 'selection'], queryFn: () => departmentService.getForSelection(), staleTime: 300_000 });

  const columns = useMemo<ColumnsType<Row>>(() => {
    const fixed: ColumnsType<Row> = [
      {
        title: 'Employee',
        fixed: 'left',
        width: 220,
        render: (_, r) => (
          <a onClick={() => navigate(`${COMPETENCY_BASE}/employees/${r.id}`)}>
            {r.employee_number} - {r.full_name}
          </a>
        ),
      },
      { title: 'Job description', dataIndex: 'job_profile_code', fixed: 'left', width: 120 },
      {
        title: 'Overall',
        dataIndex: 'overall',
        fixed: 'left',
        width: 90,
        render: (v: GapStatus) => <Tag color={GAP_COLOR[v]}>{v}</Tag>,
      },
    ];
    const dynamic: ColumnsType<Row> = (data?.columns ?? []).map((c) => ({
      key: c.key,
      title: (
        <Tooltip title={`${c.requirement_type}: ${c.name}`}>
          <div style={{ maxWidth: 110, whiteSpace: 'normal', fontSize: 12 }}>
            <Text type="secondary" style={{ fontSize: 10 }}>
              {c.requirement_type}
            </Text>
            <br />
            {c.name}
          </div>
        </Tooltip>
      ),
      width: 120,
      align: 'center' as const,
      render: (_: unknown, r: Row) => {
        const cell = r.cells[c.key];
        if (!cell) return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={`${cell.detail}${cell.mandatory ? '' : ' (optional)'}`}>
            <Tag color={GAP_COLOR[cell.status]} style={{ margin: 0, opacity: cell.mandatory ? 1 : 0.6 }}>
              {SHORT[cell.status]}
            </Tag>
          </Tooltip>
        );
      },
    }));
    return [...fixed, ...dynamic];
  }, [data, navigate]);

  const exportExcel = () => {
    if (!data) return;
    const cols = [
      { key: 'employee', title: 'Employee', dataIndex: 'employee' },
      { key: 'department', title: 'Department', dataIndex: 'department' },
      { key: 'jd', title: 'Job description', dataIndex: 'jd' },
      { key: 'overall', title: 'Overall', dataIndex: 'overall' },
      ...data.columns.map((c) => ({ key: c.key, title: `${c.requirement_type}: ${c.name}`, dataIndex: c.key })),
    ];
    const rows = data.rows.map((r) => ({
      employee: `${r.employee_number} - ${r.full_name}`,
      department: r.department_name ?? '',
      jd: r.job_profile_code ?? '',
      overall: r.overall,
      ...Object.fromEntries(data.columns.map((c) => [c.key, r.cells[c.key] ? `${r.cells[c.key].status} - ${r.cells[c.key].detail}` : ''])),
    }));
    void downloadXlsx(`competency-gap-matrix-${new Date().toISOString().slice(0, 10)}`, cols, rows);
  };

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Competency gap matrix"
        extra={
          <Button icon={<DownloadOutlined />} onClick={exportExcel} disabled={!data?.rows.length}>
            Excel
          </Button>
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            allowClear
            placeholder="Department"
            style={{ width: 220 }}
            value={departmentId}
            onChange={setDepartmentId}
            showSearch
            optionFilterProp="label"
            options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
          />
          <Select
            allowClear
            placeholder="Job description"
            style={{ width: 260 }}
            value={jobProfileId}
            onChange={setJobProfileId}
            showSearch
            optionFilterProp="label"
            options={profiles.map((p) => ({ value: p.id, label: `${p.code} - ${p.title}` }))}
          />
          <Select allowClear placeholder="Employees with" style={{ width: 170 }} value={status} onChange={setStatus} options={STATUSES.map((s) => ({ value: s, label: s }))} />
          {data && (
            <Space size={4}>
              {STATUSES.map((s) => (
                <Tag key={s} color={GAP_COLOR[s]}>
                  {s}: {data.summary?.[s] ?? 0}
                </Tag>
              ))}
            </Space>
          )}
        </Space>
        {!isLoading && data && !data.columns.length ? (
          <Empty description="Assign job descriptions with requirements to employees to see the matrix." />
        ) : (
          <Table
            rowKey="id"
            size="small"
            bordered
            loading={isLoading}
            dataSource={data?.rows ?? []}
            columns={columns}
            scroll={{ x: 430 + (data?.columns.length ?? 0) * 120, y: 'calc(100vh - 360px)' }}
            pagination={{ defaultPageSize: 50 }}
          />
        )}
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          OK met | GAP missing or below the required level | EXP? expires within 60 days | EXP expired. Faded tags are optional requirements.
        </Text>
      </Card>
    </div>
  );
};

export default GapMatrixPage;
